/**
 * The JSON contract of the music video routes (spec `2026-10-09-music24-music-video-editor-design.md`
 * §3.2, §3.3, §4.3, §4.4, §4.7): "Add yours", the footage, the cuts of the editor and the music
 * video field of a song. music24-api answers these shapes; the web app and the native apps read
 * them. Times are ISO strings, ids are uuids. Only type imports and constants, so every project may
 * read this file.
 */
import type { AddedAssetTheme, AddedVideoWarning, KitAssetSource, KitAssetYouTube, SoundCheck } from './addedAssets';
import type { DirectedCastEntry } from './directedVideoApi';
import type { MusicVideoDoc, RecutChip } from './musicVideoDoc';

// ---------------------------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------------------------

/** The `cause.code` of every refusal of the music video routes. */
export const MUSIC_VIDEO_ERROR_CODES = {
  /** 400: the asset type takes no "Add yours", or not this input (a YouTube link for the Canvas). */
  addedNotAllowed: 'M24_ADDED_NOT_ALLOWED',
  /** 400: the file is over the cap of the asset type. */
  addedTooLarge: 'M24_ADDED_TOO_LARGE',
  /** 400: the file type is not allowed for the asset type. */
  addedType: 'M24_ADDED_TYPE',
  /** 400: the uploaded video fails a rule of the asset type; `cause.reason` is an `AddedVideoRefusal`. */
  addedRefused: 'M24_ADDED_REFUSED',
  /** 400: the text is not a YouTube video link. */
  addedYouTubeInvalid: 'M24_ADDED_YOUTUBE_INVALID',
  /** 400: YouTube does not know the video (oEmbed 404). */
  addedYouTubeNotFound: 'M24_ADDED_YOUTUBE_NOT_FOUND',
  /** 400: the video is private or does not allow an embed (oEmbed 401 / 403). */
  addedYouTubePrivate: 'M24_ADDED_YOUTUBE_PRIVATE',
  /** 400: the version was made by Music24, so "Remove" does not apply. */
  addedNotAdded: 'M24_ADDED_NOT_ADDED',
  /** 409: the revision of the request is not the stored revision. */
  cutStale: 'M24_CUT_STALE',
  /** 400: the document fails `parseMusicVideoDoc` or is over `MUSIC_VIDEO_DOC_MAX_BYTES`. */
  cutDocInvalid: 'M24_CUT_DOC_INVALID',
  /** 409: the director writes the cut now. */
  cutWorking: 'M24_CUT_WORKING',
  /**
   * 429: more than `MUSIC_VIDEO_RECUTS_PER_HOUR` director runs (first cuts and recuts) of the song in
   * one hour, or more than `MUSIC_VIDEO_DIRECTOR_RUNS_PER_HOUR_PER_ACCOUNT` of the artist.
   */
  cutRecutLimit: 'M24_CUT_RECUT_LIMIT',
  /** 409: the song has `MUSIC_VIDEO_CUTS_PER_SONG` cuts and each one was made or is with the director. Delete one first. */
  cutLimit: 'M24_CUT_LIMIT',
  /** 400: a clip of the cut was archived or is not ready; make refuses it. */
  footageGone: 'M24_FOOTAGE_GONE',
  /** 409: `FOOTAGE_AI_WORKING_LIMIT` AI clips of the account are being made. */
  footageBusy: 'M24_FOOTAGE_BUSY',
  /** 400: ffprobe cannot read the uploaded clip. */
  footageUnreadable: 'M24_FOOTAGE_UNREADABLE',
  /** 400: the clip is over `FOOTAGE_UPLOAD_MAX_BYTES`. */
  footageTooLarge: 'M24_FOOTAGE_TOO_LARGE',
  /** 400: the clip type is not MP4 or MOV. */
  footageType: 'M24_FOOTAGE_TYPE',
  /** 400: a clip of the cut is still being prepared for the render (no proxy yet); make refuses it. `cause.reason`: the uuids. */
  footageProcessing: 'M24_FOOTAGE_PROCESSING',
  /**
   * 402 (Free, trial) or 409 (a paid plan): the upload would take the footage of the artist over its
   * storage limit (`footageStorageLimitBytes`). The cause has `usedBytes` and `limitBytes`.
   */
  footageQuota: 'M24_FOOTAGE_QUOTA',
  /**
   * 409: the added video has a live YouTube or Facebook publish record (not removed, not canceled).
   * Cancel or remove the publish first, so no record is lost.
   */
  addedPublished: 'M24_ADDED_PUBLISHED',
  /** 400: a one-shot music video request; the editor makes the music video (D11). */
  useMusicVideoEditor: 'M24_USE_MUSIC_VIDEO_EDITOR',
  /** 429: more than `MUSIC_VIDEO_SHOWCASE_SEARCH_PER_MINUTE` showcase bank searches of one client address in a minute. */
  rateLimited: 'M24_RATE_LIMITED',
  /** 409: the script revision of the request is not the stored revision (directed video). */
  directedStale: 'M24_DIRECTED_STALE',
  /** 409: the director works on the directed video now, or a make of the account already runs. */
  directedBusy: 'M24_DIRECTED_BUSY',
  /** 429: the director run limit or the cast picture limit of the directed video or the account. */
  directedLimit: 'M24_DIRECTED_LIMIT',
  /** 400: the call is not allowed in this status of the directed video. */
  directedStep: 'M24_DIRECTED_STEP',
  /** 400: an uploaded cast picture (any role) without the consent tick. */
  directedConsent: 'M24_DIRECTED_CONSENT',
  /**
   * 409: a directed video of the song is `making` or `rendering`. Its clips cannot be deleted, the song
   * cannot be deleted, and the editor cannot make another music video of the song until it is done.
   */
  directedMaking: 'M24_DIRECTED_MAKING',
} as const;
export type MusicVideoErrorCode = (typeof MUSIC_VIDEO_ERROR_CODES)[keyof typeof MUSIC_VIDEO_ERROR_CODES];

// ---------------------------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------------------------

/** The status words of a kit asset version (`KitAssetModel.status`). */
export type KitAssetStatusWord = 'queued' | 'planning' | 'rendering' | 'ready' | 'failed';

/** A presigned multipart upload: the client PUTs each part and keeps its `ETag`. */
export interface PresignedMultipartUploadResponse {
  uploadId: string;
  key: string;
  partSizeBytes: number;
  parts: Array<{ partNumber: number; url: string }>;
  /** ISO time. */
  expiresAt: string;
}

export interface CompleteMultipartUploadRequest {
  key: string;
  parts: Array<{ partNumber: number; etag: string }>;
}

/** A presigned GET of a file (1 hour). */
export interface PresignedFileLink {
  url: string;
  /** ISO time. */
  expiresAt: string;
}

// ---------------------------------------------------------------------------------------------
// §3.2 The music video field of a song
// ---------------------------------------------------------------------------------------------

/**
 * The current music video of a song (`song.musicVideo` of `GET .../catalog/:uuid/song` and of the
 * catalog row): the newest version, else the ready one. Null: no music video.
 */
export interface SongMusicVideoSummary {
  assetUuid: string;
  source: KitAssetSource;
  /** The version number of the kit asset (r1, r2 in the UI). */
  revision: number;
  status: KitAssetStatusWord;
  posterUrl: string | null;
  durationSec: number | null;
  /** Set for `source = youtube`. */
  youtube: KitAssetYouTube | null;
  /** True when the version has a file to play or download (an upload or a made video, not a link). */
  fileAvailable: boolean;
  /** The cut that made it (`params.musicVideoCutNumber`); null for an added video. */
  cutNumber: number | null;
  /** The sound check of an uploaded music video; null before the check and for other sources. */
  soundCheck: SoundCheck | null;
}

/** `POST .../release-kits/:catalogItemUuid/assets/:assetUuid/stream` (§4.7): the file to play. */
export type KitAssetStreamResponse = PresignedFileLink;

// ---------------------------------------------------------------------------------------------
// §3.3 Added assets (under /accounts/:accountId/release-kits/:catalogItemUuid)
// ---------------------------------------------------------------------------------------------

/** `POST /added/uploads`. The answer is a `PresignedMultipartUploadResponse`. */
export interface AddedUploadStartRequest {
  themeId: AddedAssetTheme;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/**
 * The answer of an add (201): the new ready version, and the warnings of the rules (a length that
 * differs from the song). `TAsset` is the serialized kit asset of the client.
 */
export interface AddedAssetResponse<TAsset = unknown> {
  asset: TAsset;
  warnings: AddedVideoWarning[];
}

/** One video of the connected YouTube channel, from the synced posts (no live call). */
export interface YouTubeCandidate {
  videoId: string;
  title: string;
  /** ISO time; null when the read did not give it. */
  publishedAt: string | null;
  durationSec: number | null;
  views: number | null;
  thumbnailUrl: string | null;
  privacy?: 'public' | 'unlisted' | 'private';
  /** The one video that looks most like this song (`isYouTubeSongMatch`). At most one is true. */
  bestMatch: boolean;
  /** The catalog song that already links this video, or null. */
  linkedToSong: string | null;
}

/** `GET /added/youtube-candidates?themeId=`, newest first. */
export interface YouTubeCandidatesResponse {
  connected: boolean;
  channel: { title: string; avatarUrl: string | null } | null;
  candidates: YouTubeCandidate[];
}

/** `POST /added/youtube`: a pasted link (`url`) or a candidate (`videoId`). */
export interface AddYouTubeRequest {
  themeId: AddedAssetTheme;
  url?: string;
  videoId?: string;
}

// ---------------------------------------------------------------------------------------------
// §4.3 Footage (under /accounts/:accountId)
// ---------------------------------------------------------------------------------------------

/** The look of an AI clip. */
export const FOOTAGE_AI_STYLES = ['film', 'photo', 'abstract', 'animated'] as const;
export type FootageAiStyle = (typeof FOOTAGE_AI_STYLES)[number];

/** The people in an AI clip, and the people filter of the bank. */
export const FOOTAGE_PEOPLE = ['none', 'crowd', 'person'] as const;
export type FootagePeople = (typeof FOOTAGE_PEOPLE)[number];

/** Where a footage clip comes from. `songVideo`: the music video file that the artist added to the song. */
export const FOOTAGE_SOURCES = ['upload', 'ai', 'songVideo'] as const;
export type FootageSource = (typeof FOOTAGE_SOURCES)[number];

export const FOOTAGE_STATUSES = ['queued', 'generating', 'ready', 'failed'] as const;
export type FootageStatus = (typeof FOOTAGE_STATUSES)[number];

/**
 * Why an AI clip failed. Each failure gives the credits back once. `PROVIDER_QUOTA`: the AI provider
 * refused every call for a quota or spend-cap reason for the whole wait limit (about 6 hours).
 */
export const FOOTAGE_FAIL_CODES = [
  'REFUSED',
  'QUALITY_CHECK',
  'GENERATION_FAILED',
  'BUDGET_EXCEEDED',
  'STALLED',
  'PROVIDER_QUOTA',
] as const;
export type FootageFailCode = (typeof FOOTAGE_FAIL_CODES)[number];

/** The longest text of an AI clip request. */
export const FOOTAGE_AI_PROMPT_MAX_LENGTH = 600;
/** The length of one AI clip (Veo 3.1 Fast, 16:9, 1080p). */
export const FOOTAGE_AI_CLIP_SEC = 8;
/** The AI clips of one account that may be made at one time. */
export const FOOTAGE_AI_WORKING_LIMIT = 3;
/** The cap of an uploaded clip. */
export const FOOTAGE_UPLOAD_MAX_BYTES = 4 * 1024 * 1024 * 1024;
/** The open (not complete, not stopped) footage uploads of one artist. */
export const FOOTAGE_UPLOAD_MAX_OPEN_UPLOADS = 3;
/** The footage storage of one artist on Free and the trial: every clip that is not archived, plus the open uploads. */
export const FOOTAGE_STORAGE_FREE_BYTES = 5 * 1024 * 1024 * 1024;
/** The footage storage of one artist on a paid plan. */
export const FOOTAGE_STORAGE_PAID_BYTES = 50 * 1024 * 1024 * 1024;
/** The cap of one added video ("Add yours") on Free and the trial; a paid plan has the cap of the asset type. */
export const ADDED_UPLOAD_FREE_MAX_BYTES = 5 * 1024 * 1024 * 1024;
/** An archived clip keeps its files this many days; then the daily prune deletes them when nothing uses them. */
export const FOOTAGE_ARCHIVED_FILE_DAYS = 7;

/** True for the plans that do not pay (Free and the trial). Pure. */
function isUnpaidPlan(plan: string): boolean {
  return plan === 'free' || plan === 'trial';
}

/** The footage storage limit of an artist on this plan (`Music24PlanTier`). Pure. */
export function footageStorageLimitBytes(plan: string): number {
  return isUnpaidPlan(plan) ? FOOTAGE_STORAGE_FREE_BYTES : FOOTAGE_STORAGE_PAID_BYTES;
}

/** The cap of one added video on this plan: Free and the trial get `ADDED_UPLOAD_FREE_MAX_BYTES`, else none (null). Pure. */
export function addedUploadPlanMaxBytes(plan: string): number | null {
  return isUnpaidPlan(plan) ? ADDED_UPLOAD_FREE_MAX_BYTES : null;
}
/** The types of an uploaded clip. */
export const FOOTAGE_UPLOAD_CONTENT_TYPES = ['video/mp4', 'video/quicktime'] as const;

/** One footage clip of the account (`FootageClipModel`). */
export interface SerializedFootageClip {
  uuid: string;
  /** The song the clip was made or uploaded for; null for a clip of the whole account. */
  catalogItemUuid: string | null;
  source: FootageSource;
  status: FootageStatus;
  name: string;
  prompt: string | null;
  style: FootageAiStyle | null;
  people: FootagePeople | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  focusX: number | null;
  /** The credits that the clip cost (4 for an AI clip, 0 for an upload). */
  credits: number;
  failCode: FootageFailCode | null;
  /** Presigned GETs (1 hour); null before the file exists. */
  posterUrl: string | null;
  previewUrl: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

/** `GET /footage?catalogItemUuid`: the clips of the account, the clips of this song first. */
export interface FootageListResponse {
  clips: SerializedFootageClip[];
}

/** `POST /footage/uploads`. The answer is a `PresignedMultipartUploadResponse`. */
export interface FootageUploadStartRequest {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  catalogItemUuid?: string;
}

/** `POST /footage/ai`. */
export interface AiFootageRequest {
  catalogItemUuid: string;
  prompt: string;
  style: FootageAiStyle;
  people: FootagePeople;
  /** The reference pictures (`FileModel.id` of the account, at most 3): a referenced new shot. */
  referenceFileIds?: number[];
  /** The style text of the directed video, added to the prompt of a referenced new shot. */
  styleText?: string;
}

/** The answer of an upload complete and of `POST /footage/ai` (201). */
export interface FootageClipResponse {
  clip: SerializedFootageClip;
}

/** One published clip of the bank. */
export interface BankFootageItem {
  uuid: string;
  label: string;
  durationSec: number;
  mood: string | null;
  setting: string | null;
  subject: string | null;
  focusX: number | null;
  /** Presigned GETs (1 hour). */
  posterUrl: string;
  previewUrl: string;
}

/**
 * `GET /footage/bank?q&mood&setting&people&cursor&limit&uuids`. `people: 'none'` keeps the clips with
 * no people. `uuids` (comma list in the query, at most `BANK_FOOTAGE_UUIDS_MAX`): the editor loads the
 * bank clips that a cut uses; the other filters and the cursor are ignored, and the answer holds those
 * clips, published or not, but never a failed one.
 */
export interface BankFootageQuery {
  uuids?: string[];
  q?: string;
  mood?: string;
  setting?: string;
  people?: 'none';
  cursor?: string;
  limit?: number;
}

/** The most uuids of one `GET /footage/bank?uuids=` request. */
export const BANK_FOOTAGE_UUIDS_MAX = 200;

export interface BankFootageResponse {
  items: BankFootageItem[];
  nextCursor: string | null;
}

/** `GET /footage/bank/suggest?catalogItemUuid&section=<index>`: the clips the director would pick, 12 items. */
export interface BankFootageSuggestResponse {
  items: BankFootageItem[];
}

// ---------------------------------------------------------------------------------------------
// §4.4 Cuts and the director (under /accounts/:accountId/music-video/:catalogItemUuid)
// ---------------------------------------------------------------------------------------------

export const MUSIC_VIDEO_DIRECTOR_STATES = ['idle', 'working', 'failed'] as const;
export type MusicVideoDirectorState = (typeof MUSIC_VIDEO_DIRECTOR_STATES)[number];

/** Why the director failed. A failed first cut still gets the plain cut. */
export const MUSIC_VIDEO_DIRECTOR_FAIL_CODES = [
  'DIRECTOR_FAILED',
  'AI_BUDGET_EXHAUSTED',
  'ANALYSIS_FAILED',
  'STALLED',
] as const;
export type MusicVideoDirectorFailCode = (typeof MUSIC_VIDEO_DIRECTOR_FAIL_CODES)[number];

/** The longest free text of a recut. */
export const MUSIC_VIDEO_RECUT_TEXT_MAX_LENGTH = 600;

export interface MusicVideoEditorSong {
  title: string;
  artistName: string;
  /** The relative file route (it streams the original), else the cover URL of a claimed row. */
  artworkUrl: string | null;
  /**
   * The imgproxy source of the cover; the app builds the sized imgproxy URL from it (owner rule
   * 2026-10-19). Missing in an API from before that day.
   */
  artworkSourceUrl?: string | null;
  /** The release theme of the kit (`ReleaseThemeId`); null with no kit. */
  releaseThemeId: string | null;
  /** The effective kit palette (five `#rrggbb`); null with no kit. */
  palette: string[] | null;
  /** Null with no ready master. */
  durationSec: number | null;
  masterCanPlay: boolean;
  smartLinkUuid: string | null;
}

export interface MusicVideoSongMapSection {
  index: number;
  kind: string;
  label: string;
  startSec: number;
  endSec: number;
  /** 0..1. */
  energy: number;
  mood: string | null;
}

export interface MusicVideoSongMap {
  bpm: number;
  beats: number[];
  downbeats: number[];
  sections: MusicVideoSongMapSection[];
}

export interface MusicVideoLyricLine {
  index: number;
  text: string;
  /** Null for a line with no time. */
  startMs: number | null;
  endMs: number | null;
}

export interface MusicVideoEditorLyrics {
  /** The lyric sync state of the song (`song.lyricSyncStatus`). */
  status: 'none' | 'syncing' | 'synced' | 'failed';
  lines: MusicVideoLyricLine[];
}

/** One cut in the cut menu ("Cut 2 of 2"). */
export interface MusicVideoCutSummary {
  uuid: string;
  number: number;
  revision: number;
  updatedAt: string;
  directorState: MusicVideoDirectorState;
  /** The kit asset that "Make the video" made from this cut, or null. */
  madeAssetUuid: string | null;
}

export interface MusicVideoEditorPlan {
  /** "Make the video" is open: Pro and up (`canRenderFullSongVideos`). */
  canRender: boolean;
  /** AI clips are open: Artist and up (`canMakeAiFootage`). */
  canMakeAiFootage: boolean;
  /** The video credits left (plan and packs). Null: no limit. */
  creditsLeft: number | null;
  /** The credits of "Make the video" (`VIDEO_CREDIT_COST.fullSong`). */
  renderCredits: number;
  /** The credits of one AI clip (`VIDEO_CREDIT_COST.aiClip`). */
  aiClipCredits: number;
}

/** `GET /`: everything the editor needs to open. `songMap` is null with no ready master (frame e6). */
export interface MusicVideoEditorResponse {
  song: MusicVideoEditorSong;
  songMap: MusicVideoSongMap | null;
  lyrics: MusicVideoEditorLyrics;
  cuts: MusicVideoCutSummary[];
  plan: MusicVideoEditorPlan;
  musicVideo: SongMusicVideoSummary | null;
  /**
   * The directed video of a cut that a directed video made: its cast and its style text, for a
   * referenced new shot. Null for every other song.
   */
  directed: MusicVideoEditorDirected | null;
  /** The cast of every directed video of the account, once per `fileId`, with signed `url`s. */
  castLibrary: DirectedCastLibraryEntry[];
}

/**
 * The directed video of the song in the editor answer: the most recent directed video of the song
 * that made a cut. The AI clip form of a cut in `cutUuids` opens with its cast and its style.
 */
export interface MusicVideoEditorDirected {
  uuid: string;
  /** The cast with signed `url`s. An entry whose file is gone is left out. */
  cast: DirectedCastEntry[];
  /** `directedStyleText` of the style; empty with no style. */
  styleText: string;
  /** The cuts of the song that this directed video made. */
  cutUuids: string[];
  /** The shots of the script with their cast: the form picks the cast that a section uses most. */
  shots: Array<{ startSec: number; endSec: number; castIds: string[] }>;
}

/** One picture of the cast library: a cast entry and the song of its directed video. */
export interface DirectedCastLibraryEntry extends DirectedCastEntry {
  songTitle: string;
}

/** One cut with its document (`MusicVideoCutModel`). */
export interface SerializedMusicVideoCut {
  uuid: string;
  number: number;
  /** +1 on every write; a PATCH sends the revision it read. */
  revision: number;
  doc: MusicVideoDoc;
  directorState: MusicVideoDirectorState;
  directorFailCode: MusicVideoDirectorFailCode | null;
  madeAssetUuid: string | null;
  createdById: number | null;
  createdAt: string;
  updatedAt: string;
}

/** The answer of `POST /cuts`, `GET` / `PATCH /cuts/:cutUuid` and a recut (202). */
export interface MusicVideoCutResponse {
  cut: SerializedMusicVideoCut;
}

/** `POST /cuts`: copy a cut, or (no body) make the next cut and run the first-cut director. */
export interface CreateMusicVideoCutRequest {
  fromCutUuid?: string;
}

/** `PATCH /cuts/:cutUuid`. */
export interface UpdateMusicVideoCutRequest {
  revision: number;
  doc: MusicVideoDoc;
}

/** `POST /cuts/:cutUuid/recut`. */
export interface RecutMusicVideoRequest {
  sections: number[] | 'all';
  chips: RecutChip[];
  text?: string;
}

/** `POST /cuts/:cutUuid/make` (201): the queued kit asset of the music video. */
export interface MakeMusicVideoResponse<TAsset = unknown> {
  asset: TAsset;
}
